/**
 * De volger van verzendingen (sessie 72, blok 1): zegt hoe een verzending
 * afliep (verzonden, mislukt, ingetrokken). De winkel staat in
 * `../verzendingen.ts`, de regels in de zaak in `verzending.tsx`.
 */
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";

import { v6Api, type V6Zaak } from "../api";
import { useLiveEvent } from "../live";
import { naTerugzetten, planStilleVerversing } from "../naActie";
import { v6Sleutels } from "../sleutels";
import {
  useAlleVerzendingen,
  verwijderVerzending,
  verzendingenVan,
  zetVerzending,
  type Verzending,
} from "../verzendingen";

/** Na het venster: nog even wachten op de job (elke 10 s) en Gmail. */
const NA_VENSTER_MS = 12_000;
const OPNIEUW_MS = 10_000;
/** Na zoveel keer "wacht" nog (ongeveer vijf minuten) laten we het rusten. */
const MAX_POGINGEN = 30;

/**
 * Houdt elke verzending van dit scherm bij tot hij verzonden, mislukt of
 * ingetrokken is. Eén keer per scherm (in het midden).
 */
export function useVerzendVolger(): void {
  const qc = useQueryClient();
  const alle = useAlleVerzendingen();
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const pogingen = useRef(new Map<string, number>());
  const bezig = useRef(new Set<string>());

  // De controle plant zichzelf opnieuw in zolang de rij nog wacht; via
  // een ref, zodat plan en controle elkaar niet vóór hun declaratie kennen.
  const controleerRef = useRef<(zaakId: string) => void>(() => undefined);
  const plan = useCallback((v: Verzending, na: number) => {
    if (timers.current.has(v.wachtrijId)) return;
    timers.current.set(
      v.wachtrijId,
      setTimeout(() => {
        timers.current.delete(v.wachtrijId);
        controleerRef.current(v.zaakId);
      }, Math.max(1_000, na)),
    );
  }, []);

  const controleer = useCallback(
    async (zaakId: string): Promise<void> => {
      if (bezig.current.has(zaakId)) return;
      const wachtend = verzendingenVan(qc, zaakId).filter((v) => v.stand === "wacht");
      if (wachtend.length === 0) return;
      bezig.current.add(zaakId);
      try {
        const rijen = await v6Api.wachtrij(zaakId);
        for (const v of wachtend) {
          const rij = rijen.find((r) => r.id === v.wachtrijId);
          const status = rij?.status ?? "wacht";
          if (status === "verstuurd") {
            zetVerzending(qc, v.wachtrijId, { stand: "verzonden" });
            // Eén keer de zaak: dan staat het echte bericht er, en gaat de regel weg.
            await qc.invalidateQueries({ queryKey: v6Sleutels.zaak(zaakId), exact: true });
            verwijderVerzending(qc, v.wachtrijId);
            planStilleVerversing(qc);
          } else if (status === "mislukt") {
            zetVerzending(qc, v.wachtrijId, {
              stand: "mislukt",
              reden: rij?.foutmelding?.trim() || "onbekende fout",
            });
            // De zaak komt terug in Open; het concept is weer de tekst uit de wachtrij.
            void qc.invalidateQueries({ queryKey: v6Sleutels.concept(zaakId), exact: true });
            naTerugzetten(qc, zaakId);
          } else if (status === "ingetrokken") {
            zetVerzending(qc, v.wachtrijId, { stand: "ingetrokken" });
          } else {
            const n = (pogingen.current.get(v.wachtrijId) ?? 0) + 1;
            pogingen.current.set(v.wachtrijId, n);
            if (n < MAX_POGINGEN) plan(v, OPNIEUW_MS);
          }
        }
      } catch {
        for (const v of wachtend) plan(v, OPNIEUW_MS);
      } finally {
        bezig.current.delete(zaakId);
      }
    },
    [qc, plan],
  );
  useEffect(() => {
    controleerRef.current = (id) => void controleer(id);
  }, [controleer]);

  // Elke nieuwe verzending: kijk na het venster.
  useEffect(() => {
    for (const lijst of alle.values()) {
      for (const v of lijst) {
        if (v.stand !== "wacht" || pogingen.current.has(v.wachtrijId)) continue;
        pogingen.current.set(v.wachtrijId, 0);
        plan(v, v.verstuurtOp + NA_VENSTER_MS - Date.now());
      }
    }
  }, [alle, plan]);

  useEffect(() => {
    const t = timers.current;
    return () => {
      for (const timer of t.values()) clearTimeout(timer);
      t.clear();
    };
  }, []);

  // Een antwoord of verzendstatus van een zaak met een wachtende regel:
  // meteen kijken in plaats van op de timer te wachten. Draagt het event de
  // wachtrijrij zelf (contract blok 2: `extra.wachtrij_id`, `extra.fout`),
  // dan is de uitkomst er meteen.
  useLiveEvent((event) => {
    if (event.soort !== "antwoord" && event.soort !== "verzendstatus") return;
    const rijId = typeof event.extra?.wachtrij_id === "string" ? event.extra.wachtrij_id : null;
    if (rijId) {
      for (const [zaakId, lijst] of alle) {
        const v = lijst.find((x) => x.wachtrijId === rijId && x.stand === "wacht");
        if (!v) continue;
        if (event.verzendstatus === "mislukt") {
          const fout = typeof event.extra?.fout === "string" ? event.extra.fout : null;
          zetVerzending(qc, rijId, { stand: "mislukt", reden: fout?.trim() || "onbekende fout" });
          void qc.invalidateQueries({ queryKey: v6Sleutels.concept(zaakId), exact: true });
          naTerugzetten(qc, zaakId);
        } else {
          zetVerzending(qc, rijId, { stand: "verzonden" });
          void qc
            .invalidateQueries({ queryKey: v6Sleutels.zaak(zaakId), exact: true })
            .then(() => verwijderVerzending(qc, rijId));
          planStilleVerversing(qc);
        }
        return;
      }
    }
    for (const [zaakId, lijst] of alle) {
      if (!lijst.some((v) => v.stand === "wacht")) continue;
      const zaak = qc.getQueryData<V6Zaak>(v6Sleutels.zaak(zaakId));
      if (event.zaak_id === zaakId || zaak?.gesprek_ids.includes(event.gesprek_id)) {
        void controleer(zaakId);
      }
    }
  });
}
